import { useState } from "react";
import {
  SetterOrUpdater,
  useRecoilState,
  useResetRecoilState,
  useSetRecoilState,
} from "recoil";
import { useHistory, useLocation } from "react-router-dom";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  AllstarUser,
  AllstarNovu,
  AllstarSearchSuggestionsPreview,
} from "../../State";
import { SuggestedUsersState } from "../../State/suggestedUsers";
import { User, IAuthenticatedUserQuery } from "../../Model/User";
import { IAllstarUser } from "../../@types/user";
import { WelcomeProcessingState } from "../../State/welcome";
import { SignatureSelectorState } from "../../State/loadoutSelector";
import { LedgerState } from "../../State/ledger";
import { GET_ME, LOGIN, SIGNUP } from "../../GraphQL/user";
import { WELCOME_STATE } from "../../GraphQL/welcome";
import { MY_BALANCE } from "../../GraphQL/montage";
import { URLS } from "../../Utilities/Maps";
import { noticeError } from "../../Utilities/NewRelic";
import {
  setSessionCookie,
  clearSessionCookie,
} from "../../Utilities/Auth/sessionCookie";
import { followLoginRedirect } from "../../Utilities/Auth/loginRedirect";
import { analytics } from "MVVM/Analytics";
import { useMutateFollowers } from "MVVM/Hooks/useMutateFollowers";
import {
  clearPendingClipCreations,
  reconcilePendingClipCreations,
} from "MVVM/Utilities/ClipLimits/pendingClipCreations";

const BASE_URL: string = `${process.env.REACT_APP_API_URL}`;

export const useUser = (): {
  allstarUser: IAllstarUser;
  setAllstarUser: SetterOrUpdater<any>;
  loading: boolean;
  getFreshProfile: (token?: string) => Promise<Record<string, any> | void>;
  login: (email: string, password: string) => Promise<any>;
  signup: (
    email: string,
    password: string,
    isMobileApp: boolean,
  ) => Promise<any>;

  logout: (logoutReason: string) => void;
  error: string | null;
} => {
  const location = useLocation();
  const history = useHistory();
  const userModel = User.getInstance();
  const [allstarUser, setAllstarUser] = useRecoilState(AllstarUser);
  const [, setIsProcessingWelcome] = useRecoilState(WelcomeProcessingState);

  const resetWelcomeProcessingState = useResetRecoilState(
    WelcomeProcessingState,
  );
  const resetSignatureState = useResetRecoilState(SignatureSelectorState);
  const resetUserState = useResetRecoilState(AllstarUser);
  const resetAllstarNovu = useResetRecoilState(AllstarNovu);
  const setSuggestedUsers = useSetRecoilState(SuggestedUsersState);
  const resetAllstarSearchSuggestionsPreview = useResetRecoilState(
    AllstarSearchSuggestionsPreview,
  );
  const setLedgerState = useSetRecoilState(LedgerState);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [getWelcomeState] = useLazyQuery(WELCOME_STATE, {
    fetchPolicy: "network-only",
  });
  const [getMe] = useLazyQuery<IAuthenticatedUserQuery>(GET_ME, {
    fetchPolicy: "network-only",
  });
  const [getBalanceUpdate] = useLazyQuery(MY_BALANCE, {
    fetchPolicy: "network-only",
  });
  const [loginGQL] = useLazyQuery(LOGIN, {
    fetchPolicy: "network-only",
    errorPolicy: "all",
  });
  const [signupGQL] = useMutation(SIGNUP);
  const { requestFollowers } = useMutateFollowers();

  async function getFreshProfile(token?: string | null) {
    setLoading(true);
    if (token) localStorage.setItem("token", token);
    else token = localStorage.getItem("token");
    try {
      const decoded = await userModel.DecodeToken();
      if (!decoded) {
        logout("MALFORMED TOKEN");
        return;
      }

      const isEscalated = !typeof decoded.escalated === undefined;
      const authenticatedUserQuery = await getMe();
      const userProfile = authenticatedUserQuery?.data?.me;

      if (!userProfile) {
        logout("INVALID TOKEN");
        return;
      }
      let createdDate = new Date(userProfile.user.createdDate);
      if (createdDate.toString() === "Invalid Date") {
        const numberDate = Number(userProfile.user.createdDate);
        createdDate = new Date(numberDate);
      }

      const stateData = {
        _id: userProfile.user._id,
        admin: userProfile.user.admin,
        avatar: userProfile.user.avatar,
        autoSaveClips: userProfile.autoSaveClips,
        autoclip: userProfile.autoclip,
        autoclipThreshold: userProfile.autoclipThreshold,
        cs2ClipConfig: userProfile.cs2ClipConfig,
        commentsDisabled: userProfile.user.commentsDisabled,
        credits: userProfile.user.credits,
        canManageBilling: userProfile.user.canManageBilling,
        discord: userProfile.user.discord,
        discordSignUp: userProfile.user.discordSignUp,
        faceit: userProfile.user.faceit,
        faceitMembershipTiers: userProfile.faceitMembershipTiers,
        followingCount: userProfile.user.followingCount,
        google: userProfile.user.google,
        hasClips: false,
        hasSharecodes: userProfile.metadata.hasSharecodes,
        hasUsername: userProfile.profile?.username ? true : false,
        noEmailSet: userProfile.metadata.noEmailSet,
        profile: userProfile.profile,
        rank: userProfile.user.rank,
        riot: userProfile.user.riot,
        steam: userProfile.user.steam,
        tiktok: userProfile.user.tiktok,
        epic: userProfile.user.epic,
        battlenet: userProfile.user.battlenet,
        username: userProfile.user.username,
        usernameLastChanged: userProfile.user?.usernameLastChanged,
        userGroup: userProfile.user.userGroup,
        userScopes: userProfile.user.scope,
        stats: userProfile.stats,
        clipQuota: userProfile.clipQuota,
        createdDate: createdDate,
      };

      window.rudderanalytics.identify(decoded?._id, {
        email: decoded.sub,
        userGroup: stateData?.userGroup?.type,
        username: stateData?.profile?.username || stateData?.username,
      });

      const followersResult = await requestFollowers({
        variables: { profiles: true, clips: false },
      });

      const followers = followersResult?.data?.myFollowing?.profiles;

      const shouldCheckWelcomeState =
        location.pathname.includes("/register") ||
        location.pathname.includes("/login") ||
        location.pathname.includes("/oauth");

      if (shouldCheckWelcomeState) setIsProcessingWelcome(true);

      setAllstarUser({
        escalated: isEscalated,
        loggedIn: true,
        isAdmin: decoded.admin,
        token,
        user: stateData,
        decoded: decoded,
        BASE_URL,
        myAvatar: stateData.avatar,
        followers: followers,
      });

      if (stateData.clipQuota) {
        reconcilePendingClipCreations(stateData.clipQuota.used);
      }

      if (shouldCheckWelcomeState) {
        try {
          const { data: welcomeData } = (await getWelcomeState()) as {
            data: {
              ftue: {
                hasCompleted: boolean;
              } | null;
            };
          };

          // Only show welcome for users who explicitly haven't completed it
          // Legacy users should just skip since they'll get stuck on username update
          if (welcomeData?.ftue?.hasCompleted === false) {
            if (!location.pathname.includes("welcome")) {
              return history.replace("/welcome");
            }
            return;
          }

          setIsProcessingWelcome(false);
          const next = localStorage.getItem("next");
          localStorage.removeItem("next");
          if (!followLoginRedirect(next, history.push)) {
            history.push(URLS.PROFILE);
          }
          return;
        } catch (error) {
          setIsProcessingWelcome(false);
          console.log("Error from welcome state query: ", error);
        }
      }

      const next = localStorage.getItem("next");
      localStorage.removeItem("next");
      // A dropped (non-allowlisted) target falls through as if no next existed.
      if (followLoginRedirect(next, history.push)) {
        return;
      }

      try {
        const { data } = await getBalanceUpdate({
          context: { headers: { authorization: `Bearer ${token}` } },
        });
        if (data?.myLedgerBalance?.balance !== undefined) {
          setLedgerState({ localBalance: data.myLedgerBalance.balance });
        }
      } catch (balanceError) {
        analytics.track("Error Loading Ledger Balance on Profile Fetch", {
          msg: "Generic error",
          error: balanceError,
        });
      }

      setLoading(false);

      // Callers that need the just-fetched profile (not the possibly-stale
      // atom captured in their closure) can use the return value directly.
      return stateData;
    } catch (error: any) {
      setError(error);
      setLoading(false);
    }
  }

  const login = async (email: string, password: string) => {
    const data = await loginGQL({
      variables: {
        email,
        password,
      },
    });

    const token = data?.data?.authentication;

    if (token) {
      setSessionCookie(token);
      localStorage.setItem("token", token);
      analytics.track("Login", {
        loginType: "email",
      });
      analytics.track("Login - New User Redirected", {});
      const decoded = await userModel.DecodeToken();
      if (!decoded?._id) noticeError("No user ID found in token oauth login");

      await getFreshProfile();

      const next = localStorage.getItem("next");
      if (next) {
        localStorage.removeItem("next");
        followLoginRedirect(next, history.push);
      }
      return { status: "SUCCESS" };
    }

    if (data.error)
      return { status: "FAILED", message: "Invalid email or password." };

    return { status: "FAILED", message: "Something went wrong." };
  };

  const signup = async (email: string, password: string) => {
    const signupData = await signupGQL({
      variables: {
        email,
        password,
      },
    });

    if (signupData?.data?.signup?.errors?.length) {
      return { status: "FAILED", message: "Email already exists." };
    }

    const token = signupData?.data?.signup?.token;
    localStorage.setItem("token", token);
    setSessionCookie(token);
    if (token) {
      analytics.track("Signup", {
        type: "email",
      });
      const decoded = await userModel.DecodeToken();
      if (!decoded?._id) noticeError("No user ID found in token oauth signup");

      await getFreshProfile(token);

      return { status: "SUCCESS", message: "" };
    }

    return { status: "FAILED", message: "Something went wrong." };
  };

  const EXCLUDED_NEXT_PATHS = ["/404", "/reset/form"];
  const PATHS_REQUIRE_AUTH = [
    "/support",
    "/subscriptions",
    "/connectedaccounts",
    "/accountsettings",
    "/notifications",
    "/dashboard",
  ];

  function logout(logoutReason: string) {
    localStorage.removeItem("payload");
    localStorage.removeItem("token");
    clearSessionCookie();
    clearPendingClipCreations();
    resetWelcomeProcessingState();
    resetSignatureState();
    resetUserState();
    resetAllstarNovu();
    resetAllstarSearchSuggestionsPreview();
    setSuggestedUsers((prevState) => {
      const newState = { ...prevState };
      Object.keys(newState).forEach((cacheKey) => {
        if (cacheKey.startsWith("logged-in")) {
          delete newState[cacheKey];
        }
      });
      return newState;
    });

    analytics.track("Logout", {
      logoutReason,
      id: allstarUser?.user?._id,
    });

    if (!PATHS_REQUIRE_AUTH.includes(window.location.pathname)) return;
    if (EXCLUDED_NEXT_PATHS.includes(window.location.pathname))
      window.location.href = "/login";
    return (window.location.href = `/login?next=${window.location.pathname}`);
  }

  return {
    allstarUser,
    setAllstarUser,
    getFreshProfile,
    loading,
    error,
    login,
    signup,
    logout,
  };
};
